import React, { useEffect, useState } from 'react';
import { Card, Button, Badge } from '../components/UI';
import { apiRequestFull, apiRequest } from '../utils/api';
import { AlertTriangle, UserX, Clock, CheckCircle, RefreshCw, AlertOctagon, Gavel } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHero } from '../components/PageHero';

interface AttendanceRecord {
    user_id: string;
    username: string;
    status: string;
    is_late: boolean;
    timestamp: string | null;
    has_penalty?: boolean;
}

export const AttendanceAdmin: React.FC = () => {
    const [events, setEvents] = useState<any[]>([]);
    const [selectedEventId, setSelectedEventId] = useState<string>('');
    const [records, setRecords] = useState<AttendanceRecord[]>([]);
    const [deadline, setDeadline] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [debugInfo, setDebugInfo] = useState<any>(null);

    useEffect(() => {
        apiRequest<any[]>('events.php').then(data => {
            if (Array.isArray(data)) {
                setEvents(data);
                // Auto-Select Logic (same as before)
                if (data.length > 0) {
                    const now = Date.now();
                    const twentyFourHours = 24 * 60 * 60 * 1000;
                    const expiredEvents = data.filter(evt => (new Date(evt.date).getTime() - twentyFourHours) < now);
                    
                    if (expiredEvents.length > 0) {
                        expiredEvents.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                        const cand = expiredEvents[0].id;
                        setSelectedEventId(cand);
                        loadRecords(cand);
                    } else {
                        setSelectedEventId(data[0].id);
                        loadRecords(data[0].id);
                    }
                }
            }
        });
    }, []);

    const loadRecords = async (eid: string) => {
        if (!eid) return;
        setLoading(true);
        setSelectedEventId(eid);
        try {
            const res = await apiRequestFull<any>(`attendance_admin.php?eventId=${eid}&_t=${Date.now()}`);
            const data = res.data || [];
            setRecords(Array.isArray(data) ? data : []);
            const fullResponse = res as any; 
            setDeadline(fullResponse.deadline || '');
            setDebugInfo({ msg: fullResponse.debug_message, sid: fullResponse.debug_sid });
        } catch (e: any) {
            alert(e.message);
        } finally {
            setLoading(false);
        }
    };

    const applyPenalty = async (userId: string, ruleId: string, points: number) => {
        // SECURITY CHECK FOR BAN
        if (ruleId === '3.10' || points === 0) { // Assuming 0 points usually implies Warning or Ban in this context if rule is severe
             if (!confirm("🚨 ACHTUNG: Du bist dabei, einen LIGA-AUSSCHLUSS (§3.10) oder eine schwere Sanktion zu verhängen.\n\nBist du ABSOLUT sicher?")) return;
             if (!confirm("Sicherheitsprüfung: Bestätige erneut, dass dieser Fahrer ausgeschlossen werden soll.")) return;
        } else {
             if (!confirm(`Strafe §${ruleId} (${points} Pkt) anwenden?`)) return;
        }

        try {
            await apiRequest('attendance_admin.php', 'POST', {
                userId,
                eventId: selectedEventId,
                ruleId,
                points
            });
            // Update local state to show checkmark
            setRecords(prev => prev.map(r => r.user_id === userId ? { ...r, has_penalty: true } : r));
        } catch (e: any) {
            alert(e.message);
        }
    };

    // Generic Action Buttons Component
    const PenaltyActions = ({ driver }: { driver: AttendanceRecord }) => {
        if (driver.has_penalty) {
            return <div className="text-green-500 font-bold flex items-center justify-end gap-2"><CheckCircle size={16}/> Bestraft</div>;
        }
        return (
            <div className="flex gap-2 justify-end">
                <Button size="sm" variant="danger" onClick={() => applyPenalty(driver.user_id, '3.7/3.8', -5)}>
                    §3.7/8 (-5)
                </Button>
                <Button size="sm" variant="secondary" onClick={() => applyPenalty(driver.user_id, 'Late', -3)}>
                    Late (-3)
                </Button>
                <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-900/20" onClick={() => applyPenalty(driver.user_id, '3.10', 0)}>
                    <AlertOctagon size={14} className="mr-1"/> §3.10 (Ban)
                </Button>
            </div>
        );
    };

    const safeRecords = Array.isArray(records) ? records : [];
    const missingDrivers = safeRecords.filter(r => r.status === 'NO_RESPONSE');
    const lateDrivers = safeRecords.filter(r => r.is_late);

    return (
        <div className="mx-auto max-w-7xl space-y-6 animate-fade-in pb-20">
            <PageHero
                eyebrow="Attendance Control"
                title="Anwesenheit"
                description="Rückmeldungen, verspätete Anmeldungen und offene Antworten vor dem Rennen zentral prüfen."
                icon={Clock}
                accent="#f97316"
                stats={[{ label: 'Fahrer', value: safeRecords.length }, { label: 'Ohne Antwort', value: missingDrivers.length }, { label: 'Verspätet', value: lateDrivers.length }]}
                actions={<Button variant="secondary" size="sm" icon={RefreshCw} onClick={() => loadRecords(selectedEventId)}>Aktualisieren</Button>}
            />
            
            <Card className="rounded-[1.75rem] border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017] shadow-[0_24px_70px_rgba(0,0,0,0.22)]">
                <div className="mb-6">
                    <label className="block text-gray-400 text-sm font-bold mb-2 uppercase">Rennen auswählen</label>
                    <select 
                        className="w-full bg-gray-900 border border-gray-700 rounded p-3 text-white"
                        value={selectedEventId}
                        onChange={e => loadRecords(e.target.value)}
                    >
                        <option value="">-- Rennen wählen --</option>
                        {events.map(e => (
                            <option key={e.id} value={e.id}>{e.trackLocation} (R{e.roundNumber}) - {e.seasonName}</option>
                        ))}
                    </select>
                </div>

                {loading ? <div className="p-10 text-center text-gray-500">Wird geladen …</div> : selectedEventId && (
                    <div className="space-y-8 animate-fade-in">
                        <div className="bg-gray-800 p-4 rounded border border-gray-700 flex justify-between items-center">
                            <div className="text-sm text-gray-400">
                                Frist: <span className="text-white font-mono font-bold">{deadline}</span>
                            </div>
                            {debugInfo?.msg && <span className="text-xs text-yellow-500">{debugInfo.msg}</span>}
                        </div>

                        {safeRecords.length === 0 ? (
                            <div className="text-center p-8 text-gray-500">Keine Fahrer für diese Saison oder dieses Rennen gefunden.</div>
                        ) : (
                            <>
                                {/* MISSING SECTION */}
                                <div>
                                    <h3 className="text-lg font-bold text-red-400 mb-3 flex items-center gap-2"><UserX size={18}/> Fehlend / ohne Antwort ({missingDrivers.length})</h3>
                                    <div className="bg-gray-900 rounded border border-gray-800 overflow-hidden">
                                        {missingDrivers.length === 0 ? <div className="p-4 text-green-500 italic flex gap-2"><CheckCircle size={16}/> Alles erledigt.</div> : (
                                            <table className="w-full text-left text-sm">
                                                <thead className="bg-gray-800 text-gray-400 uppercase text-xs">
                                                    <tr>
                                                        <th className="p-3">Fahrer</th>
                                                        <th className="p-3 text-right">Aktion</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {missingDrivers.map(d => (
                                                        <tr key={d.user_id} className="border-b border-gray-800 last:border-0 hover:bg-gray-800/50">
                                                            <td className="p-3 font-bold text-white">{d.username}</td>
                                                            <td className="p-3"><PenaltyActions driver={d} /></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </div>

                                {/* LATE SECTION */}
                                <div>
                                    <h3 className="text-lg font-bold text-yellow-500 mb-3 flex items-center gap-2"><Clock size={18}/> Verspätete Anmeldungen ({lateDrivers.length})</h3>
                                    <div className="bg-gray-900 rounded border border-gray-800 overflow-hidden">
                                        {lateDrivers.length === 0 ? <div className="p-4 text-gray-500 italic">Keine verspäteten Anmeldungen.</div> : (
                                            <table className="w-full text-left text-sm">
                                                <thead className="bg-gray-800 text-gray-400 uppercase text-xs">
                                                    <tr>
                                                        <th className="p-3">Driver</th>
                                                        <th className="p-3">Registered At</th>
                                                        <th className="p-3 text-right">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {lateDrivers.map(d => (
                                                        <tr key={d.user_id} className="border-b border-gray-800 last:border-0 hover:bg-gray-800/50">
                                                            <td className="p-3 font-bold text-white">{d.username}</td>
                                                            <td className="p-3 text-gray-400 font-mono">{d.timestamp}</td>
                                                            <td className="p-3"><PenaltyActions driver={d} /></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
};
